import React, { useContext, useState } from "react";
import {
  Chip,
  IconButton,
  makeStyles,
  Avatar,
  Typography,
  Box,
  Tooltip,
  Card,
  CardContent,
} from "@material-ui/core";

import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";

import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ForumIcon from "@material-ui/icons/Forum";
import PeopleIcon from "@material-ui/icons/People";

import ConfirmationModal from "../../components/ConfirmationModal";
import { markChatRead } from "../../services/chats.api";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: theme.palette.boxlist, //DARK MODE PLW DESIGN//
  },
  chatList: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    ...theme.scrollbarStyles,
  },
  listItem: {
    cursor: "pointer",
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateX(4px)",
    },
  },
  chatCard: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.primary.main,
    },
  },
  chatCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "20",
    boxShadow: theme.shadows[2],
  },
  chatAvatar: {
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
  chatTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  chatSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  actionButton: {
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "scale(1.1)",
    },
  },
  editButton: {
    color: theme.palette.info.main,
    "&:hover": {
      backgroundColor: theme.palette.info.light + "20",
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.error.light + "20",
    },
  },
  unreadBadge: {
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    fontWeight: 600,
    minWidth: 20,
    height: 20,
  },
  participantCount: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
}));

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});

  const { id } = useParams();

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      try {
        await markChatRead(chat.id, { userId: user.id });
      } catch (err) {}
    }

    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
      handleSelectChat(chat);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
  };

  const unreadMessages = (chat) => {
    const currentUser = chat.users.find((u) => u.userId === user.id);
    return currentUser.unreads;
  };

  const getParticipantCount = (chat) => {
    return chat.users ? chat.users.length : 0;
  };

  const getLastMessageInfo = (chat) => {
    if (chat.lastMessage && chat.lastMessage !== "") {
      return {
        message: chat.lastMessage,
        time: datetimeToClient(chat.updatedAt)
      };
    }
    return null;
  };

  const isSelected = (chat) => chat.uuid === id;

  return (
    <>
      <ConfirmationModal
        title={"Eliminar Conversación"}
        open={confirmationModal}
        onClose={setConfirmModalOpen}
        onConfirm={handleDelete}
      >
        <Box>
          <Typography variant="h6" gutterBottom color="error">
            <span role="img" aria-label="warning">⚠️</span> ¿Estás seguro de que quieres eliminar esta conversación?
          </Typography>
          <Typography variant="body1" paragraph>
            Esta acción <strong>no se puede deshacer</strong> y eliminará permanentemente:
          </Typography>
          <Box component="ul" pl={2}>
            <Typography component="li" variant="body2">
              Todos los mensajes de la conversación
            </Typography>
            <Typography component="li" variant="body2">
              El historial completo de chat
            </Typography>
            <Typography component="li" variant="body2">
              El acceso para todos los participantes
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            <strong>Conversación:</strong> {selectedChat.title || 'Sin título'}
          </Typography>
        </Box>
      </ConfirmationModal>
      <div className={classes.mainContainer}>
        <div className={classes.chatList}>
          {Array.isArray(chats) && chats.length > 0 ? (
            chats.map((chat, key) => {
              const unreads = unreadMessages(chat);
              const participantCount = getParticipantCount(chat);
              const lastMessageInfo = getLastMessageInfo(chat);
              const selected = isSelected(chat);

              return (
                <Card
                  key={key}
                  className={`${classes.chatCard} ${selected ? classes.chatCardSelected : ''}`}
                  onClick={() => goToMessages(chat)}
                  style={{ marginBottom: 8, cursor: 'pointer' }}
                >
                  <CardContent style={{ padding: 12 }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                      <Box display="flex" alignItems="flex-start" flex={1}>
                        <Avatar className={classes.chatAvatar}>
                          <ForumIcon />
                        </Avatar>
                        
                        <Box flex={1} minWidth={0}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle1" className={classes.chatTitle}>
                              {chat.title}
                            </Typography>
                            
                            {unreads > 0 && (
                              <Chip
                                size="small"
                                label={unreads}
                                className={classes.unreadBadge}
                              />
                            )}
                          </Box>
                          
                          <Box className={classes.participantCount}>
                            <PeopleIcon style={{ fontSize: 14, marginRight: 4 }} />
                            <Typography variant="caption" color="textSecondary">
                              {participantCount} participante{participantCount !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          
                          {lastMessageInfo && (
                            <Typography variant="body2" className={classes.chatSubtitle}>
                              {lastMessageInfo.time}: {lastMessageInfo.message}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {chat.ownerId === user.id && (
                        <Box className={classes.actionButtons}>
                          <Tooltip title="Editar conversación">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                goToMessages(chat).then(() => {
                                  handleEditChat(chat);
                                });
                              }}
                              className={`${classes.actionButton} ${classes.editButton}`}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Eliminar conversación">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedChat(chat);
                                setConfirmModalOpen(true);
                              }}
                              className={`${classes.actionButton} ${classes.deleteButton}`}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              height="100%"
              p={3}
            >
              <ForumIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No hay conversaciones
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Crea tu primera conversación usando el botón "Nueva Conversación"
              </Typography>
            </Box>
          )}
        </div>
      </div>
    </>
  );
}
